import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import axios from 'axios';
import './index.css';
import { AppProvider } from 'utils/contexts/app-context';
import * as LocalStorage from 'utils/local-storage';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import store from './state/store';
import * as serviceWorker from './serviceWorker';
import 'utils/i18n';
import ErrorBoundary from './error-boundary';
import PageLoadingFallback from 'components/page-loading-fallback';
import Targets from './utils/targets';
import FeatureFlags from './utils/feature-flags';
import App from './App';

const {
  REACT_APP_SERVER_URL = '',
  REACT_APP_NODE_SERVER_URL = ''
} = process.env;

const baseURL = `${REACT_APP_NODE_SERVER_URL}/app`;
axios.defaults.baseURL = baseURL;

const initialToken = LocalStorage.getToken();
const initialRefreshToken = LocalStorage.getRefreshToken();

const defaultFlags = target => {
  const isSecurity = target === Targets.Security;
  const isRecruitment = target === Targets.Recruitment;

  return [
    { name: FeatureFlags.AllowRegister, isActive: isRecruitment },
    { name: FeatureFlags.AllowDashboard, isActive: isRecruitment },
    { name: FeatureFlags.AllowSearchAll, isActive: isRecruitment },
    { name: FeatureFlags.AllowTalents, isActive: isRecruitment },
    { name: FeatureFlags.AllowBilling, isActive: isRecruitment },
    { name: FeatureFlags.AllowIntegrations, isActive: isRecruitment },
    { name: FeatureFlags.AllowValidations, isActive: isSecurity },
    { name: FeatureFlags.AllowTwitter, isActive: isSecurity },
    { name: FeatureFlags.AllowReferences, isActive: isRecruitment },
  ];
};

const appShell = (
  <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}>
    <AppProvider
      initialToken={initialToken}
      initialRefreshToken={initialRefreshToken}
      target={Targets.Security}
    >
      <App defaultFlags={defaultFlags(Targets.Security)} />
    </AppProvider>
  </Suspense>
);

function SecurityRedirect() {
  const location = useLocation();
  const newPath =
    (location.pathname.replace('/security', '') || '/') + location.search;
  return (
    <Navigate
      to={{ pathname: newPath, hash: location.hash }}
      replace
    />
  );
}

function RootRoutes() {
  return (
    <Routes>
      <Route path="/security/*" element={<SecurityRedirect />} />
      <Route path="/*" element={appShell} />
    </Routes>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <ErrorBoundary>
    <Provider store={store}>
      <BrowserRouter>
        <RootRoutes />
      </BrowserRouter>
    </Provider>
  </ErrorBoundary>
);

serviceWorker.unregister();
